/**
 * Application entry point
 */
// Console guard MUST be the very first import — zero dependencies,
// overrides console.* in production before any other module can log.
import '../core/services/consoleGuard.js'
import { createApp } from 'vue'
import App from './App.vue'
import router from './router/index.js'
import pinia from './store/index.js'
import { useAuthStore } from './store/index.js'
import { setAuthStore } from './router/guards.js'
import { initializeFirebase } from '../core/firebase/firebase.js'
import { validateEnv } from '../core/config/env.js'
import logger from '../core/services/logger.js'
import { TutorialPlugin } from '../core/tutorials/index.js'
import './styles/main.css'

const log = logger.createScoped('Main')

// Create Vue app
const app = createApp(App)

// Register Pinia first (required for stores)
app.use(pinia)

// Register Tutorial Plugin (v-tutorial directive + overlay)
app.use(TutorialPlugin)

// Initialize auth store and connect to guards
const authStore = useAuthStore()
setAuthStore(authStore)

// Initialize Firebase and auth bootstrap
async function initializeApp() {
  log.info('Initializing application...')

  // Validate environment
  if (!validateEnv()) {
    log.warn('Environment validation failed - Firebase may not work correctly')
  }

  // Initialize Firebase
  try {
    await initializeFirebase()
    log.info('Firebase initialized')

    // Start auth bootstrap (non-blocking)
    // Guards will await this if they need to
    authStore.initAuthBootstrap()
  } catch (error) {
    log.error('Failed to initialize Firebase', error)
    // Set auth as resolved (signed out) to prevent infinite loading
    authStore.setSignedOut()
    authStore.setLoading(false)
  }
}

// Start initialization immediately
initializeApp()

// Register router
app.use(router)

// Mount app
app.mount('#app')

// ===== DEV MODE: Unregister service workers =====
if ('serviceWorker' in navigator && import.meta.env.DEV) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister()
      log.debug('Unregistered service worker for development')
    }
  })
}
